import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable, switchMap, tap } from 'rxjs';
import { FeesWaiversExemptionsResponse } from '../../../fees-waivers-exemptions/models/fees-waivers-exemptions-response';
import { FeesWaiversExemptionsRequest } from '../../../fees-waivers-exemptions/models/fees-waivers-exemptions-request';
import { GeneralInfoResponse } from '../../../general-info/models/general-info-response';
import { GeneralInfoRequest } from '../../../general-info/models/general-info-request';
import { StepValidationResult } from '../../../../shared/models/step-validation-result';
import { ApiRouteConstants } from '../../../../../../core/constants/api-routes';
import { ApplicantInfoRequest } from '../../../applicant-info/models/applicant-info-request';
import { ApplicantInfoResponse } from '../../../applicant-info/models/applicant-info-response';
import { ScheduleHAdditionalInformationRequest } from '../../../schedule-h-additional-information/models/schedule-h-additional-information-request';
import { ScheduleHAdditionalInformationResponse } from '../../../schedule-h-additional-information/models/schedule-h-additional-information-response';
import { SchedulesDAndHRequest } from '../../../schedules-d-and-h/models/schedules-d-and-h-request';
import { RegulatoryInfoResponse } from '../../../regulatory-info/models/regulatory-info-response';
import { RegulatoryInfoRequest } from '../../../regulatory-info/models/regulatory-info-request';
import { CertificationInfoResponse } from '../../../certification-info/models/certification-info-response';
import { CertificationInfoRequest } from '../../../certification-info/models/certification-info-request';
import { AttachmentsAndWaiversResponse } from '../../../attachments-and-waivers/models/attachments-and-waivers-response';
import { AttachmentsAndWaiversRequest } from '../../../attachments-and-waivers/models/attachments-and-waivers-request';
import { SchedulesDAndHResponse } from '../../../schedules-d-and-h/models/schedules-d-and-h-response';
import { ScheduleKResponse } from '../../../schedule-k/models/schedule-k-response';
import { ScheduleKRequest } from '../../../schedule-k/models/schedule-k-request';
import { StepNavigationService } from '../../../../shared/services/step-navigation/step-navigation.service';

@Injectable({
    providedIn: 'root',
})
export class DirectEntryService {
    private readonly applicationApiRoute = ApiRouteConstants.Application;

    constructor(
        private readonly http: HttpClient,
        private readonly stepNavigationService: StepNavigationService,
    ) {}

    getGeneralInfo(applicationId: string): Observable<GeneralInfoResponse> {
        const fullRoute =
            this.applicationApiRoute.getFullRouteWithApplicationId(
                'generalInfo',
                applicationId,
            );

        return this.http.get<GeneralInfoResponse>(fullRoute);
    }

    saveGeneralInfo(
        request: GeneralInfoRequest,
    ): Observable<StepValidationResult> {
        const fullRoute =
            this.applicationApiRoute.getFullRouteWithApplicationId(
                'generalInfo',
                request.applicationId,
            );

        return this.http.post<StepValidationResult>(fullRoute, request);
    }

    getFeesWaiversExemptions(
        applicationId: string,
    ): Observable<FeesWaiversExemptionsResponse> {
        const fullRoute =
            this.applicationApiRoute.getFullRouteWithApplicationId(
                'feesWaiversExemptions',
                applicationId,
            );

        return this.http.get<FeesWaiversExemptionsResponse>(fullRoute);
    }

    saveFeesWaiversExemptions(
        request: FeesWaiversExemptionsRequest,
    ): Observable<StepValidationResult> {
        const fullRoute =
            this.applicationApiRoute.getFullRouteWithApplicationId(
                'feesWaiversExemptions',
                request.applicationId,
            );

        return this.http.post<StepValidationResult>(fullRoute, request);
    }

    getApplicantInfo(applicationId: string): Observable<ApplicantInfoResponse> {
        const fullRoute =
            this.applicationApiRoute.getFullRouteWithApplicationId(
                'applicantInfo',
                applicationId,
            );

        return this.http.get<ApplicantInfoResponse>(fullRoute);
    }

    saveApplicantInfo(
        request: ApplicantInfoRequest,
    ): Observable<StepValidationResult> {
        const fullRoute =
            this.applicationApiRoute.getFullRouteWithApplicationId(
                'applicantInfo',
                request.applicationId,
            );

        return this.http.post<StepValidationResult>(fullRoute, request);
    }

    getRegulatoryInfo(
        applicationId: string,
    ): Observable<RegulatoryInfoResponse> {
        const fullRoute =
            this.applicationApiRoute.getFullRouteWithApplicationId(
                'regulatoryInfo',
                applicationId,
            );

        return this.http.get<RegulatoryInfoResponse>(fullRoute);
    }

    saveRegulatoryInfo(
        request: RegulatoryInfoRequest,
    ): Observable<StepValidationResult> {
        const fullRoute =
            this.applicationApiRoute.getFullRouteWithApplicationId(
                'regulatoryInfo',
                request.applicationId,
            );

        return this.http.post<StepValidationResult>(fullRoute, request);
    }

    getCertificationInfo(
        applicationId: string,
    ): Observable<CertificationInfoResponse> {
        const fullRoute =
            this.applicationApiRoute.getFullRouteWithApplicationId(
                'certificationInfo',
                applicationId,
            );

        return this.http.get<CertificationInfoResponse>(fullRoute);
    }

    saveCertificationInfo(
        request: CertificationInfoRequest,
    ): Observable<StepValidationResult> {
        const fullRoute =
            this.applicationApiRoute.getFullRouteWithApplicationId(
                'certificationInfo',
                request.applicationId,
            );

        return this.http.post<StepValidationResult>(fullRoute, request);
    }

    getScheduleHAdditionalInformation(
        applicationId: string,
    ): Observable<ScheduleHAdditionalInformationResponse> {
        const fullRoute =
            this.applicationApiRoute.getFullRouteWithApplicationId(
                'scheduleHAdditionalInformation',
                applicationId,
            );

        return this.http.get<ScheduleHAdditionalInformationResponse>(fullRoute);
    }

    saveScheduleHAdditionalInformation(
        request: ScheduleHAdditionalInformationRequest,
    ): Observable<StepValidationResult> {
        const fullRoute =
            this.applicationApiRoute.getFullRouteWithApplicationId(
                'scheduleHAdditionalInformation',
                request.applicationId,
            );

        return this.http.post<StepValidationResult>(fullRoute, request);
    }

    getSchedulesDAndH(
        applicationId: string,
    ): Observable<SchedulesDAndHResponse> {
        const fullRoute =
            this.applicationApiRoute.getFullRouteWithApplicationId(
                'scheduleDAndH',
                applicationId,
            );

        return this.http.get<SchedulesDAndHResponse>(fullRoute);
    }

    saveSchedulesDAndH(
        request: SchedulesDAndHRequest,
    ): Observable<StepValidationResult> {
        const fullRoute =
            this.applicationApiRoute.getFullRouteWithApplicationId(
                'scheduleDAndH',
                request.applicationId,
            );

        return this.http.post<StepValidationResult>(fullRoute, request);
    }

    getScheduleK(applicationId: string): Observable<ScheduleKResponse> {
        const fullRoute =
            this.applicationApiRoute.getFullRouteWithApplicationId(
                'scheduleK',
                applicationId,
            );

        return this.http.get<ScheduleKResponse>(fullRoute);
    }

    saveScheduleK(request: ScheduleKRequest): Observable<StepValidationResult> {
        const fullRoute =
            this.applicationApiRoute.getFullRouteWithApplicationId(
                'scheduleK',
                request.applicationId,
            );
        return this.http.post<StepValidationResult>(fullRoute, request);
    }

    getAttachmentsAndWaivers(
        applicationId: string,
    ): Observable<AttachmentsAndWaiversResponse> {
        const fullRoute =
            this.applicationApiRoute.getFullRouteWithApplicationId(
                'attachmentsAndWaivers',
                applicationId,
            );
        return this.http.get<AttachmentsAndWaiversResponse>(fullRoute);
    }

    saveAttachmentsAndWaivers(
        request: AttachmentsAndWaiversRequest,
    ): Observable<StepValidationResult> {
        const fullRoute =
            this.applicationApiRoute.getFullRouteWithApplicationId(
                'attachmentsAndWaivers',
                request.applicationId,
            );
        return this.http.post<StepValidationResult>(fullRoute, request);
    }

    getStepValidations(
        applicationId: string,
    ): Observable<StepValidationResult[]> {
        return this.http.get<StepValidationResult[]>(
            this.applicationApiRoute.getFullRouteWithApplicationId(
                'validateSteps',
                applicationId,
            ),
        );
    }

    /**
     * Sets the current application step validations by fetching them from the API.
     *
     * @param applicationId The ID of the application for which to set the step validations.
     * @param navigateToFirstStep Whether to navigate to the first invalid step after setting validations. Default is true.
     */
    getAndSetStepValidations(
        applicationId: string,
        navigateToFirstStep: boolean = true,
    ): Observable<void> {
        return this.getStepValidations(applicationId).pipe(
            tap((stepValidations: StepValidationResult[]) => {
                this.stepNavigationService.setStepValidations(
                    stepValidations,
                    navigateToFirstStep,
                );
            }),
            map(() => void 0),
        );
    }

    /**
     * Sets the direct entry steps and their validations for the given application.
     *
     * @param applicationId The ID of the application for which to set the steps and validations.
     * @param navigateToFirstStep Whether to navigate to the first invalid step after setting validations. Default is true.
     * @return An Observable that completes when the steps and validations have been set.
     */
    getAndSetDirectEntryStepsWithValidations(
        applicationId: string,
        navigateToFirstStep: boolean = true,
    ): Observable<void> {
        return this.stepNavigationService
            .setDirectEntrySteps(applicationId)
            .pipe(
                switchMap(() =>
                    this.getAndSetStepValidations(
                        applicationId,
                        navigateToFirstStep,
                    ),
                ),
            );
    }
}
